/* eslint-disable @typescript-eslint/no-magic-numbers */
import styled from "styled-components";

import { SVG_MUI } from "@ryde/assets";
import {
  RideDelayStatusEnum,
  RideRouteCompletionStatusEnum,
  RideStartStatusEnum,
} from "@ryde/shared";
import { blueGreyMui, greenMui, orangeMui, redMui } from "@ryde/theme";

export type LeftLineColor = "normal" | "darkRed" | "red" | "orange" | "violet";

const LiveMapDelayStatusIcon = styled.span`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }
`;

export const RideStatusDelayToColor = {
  [RideDelayStatusEnum.NO_SHOW]: redMui._900,
  [RideDelayStatusEnum.DELAYED]: redMui.A400,
  [RideDelayStatusEnum.MAYBE_LATE]: orangeMui.A400,
  [RideDelayStatusEnum.EARLY]: orangeMui.A200,
  [RideDelayStatusEnum.ON_TIME]: greenMui._600,
  [RideDelayStatusEnum.FUTURE]: blueGreyMui._500,
  [RideDelayStatusEnum.PAST]: blueGreyMui._400,
  [RideDelayStatusEnum.NOT_TRACKED]: blueGreyMui._300,
} as const;

const parseHexRgb = (hex: string): { r: number; g: number; b: number } => {
  const raw = hex.replace("#", "").trim();
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map(char => char + char)
          .join("")
      : raw.slice(0, 6);

  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
};

const mixSrgbHexWithWhite = (
  hex: string,
  colorWeightPercent: number,
): string => {
  const ratio = colorWeightPercent / 100;
  const { r, g, b } = parseHexRgb(hex);
  const mixChannel = (channel: number): number =>
    Math.round(channel * ratio + 255 * (1 - ratio));
  const toHexChannel = (value: number): string =>
    value.toString(16).padStart(2, "0");

  return `#${toHexChannel(mixChannel(r))}${toHexChannel(mixChannel(g))}${toHexChannel(mixChannel(b))}`;
};

const hexColorToRgba = (hex: string, alpha: number): string => {
  const { r, g, b } = parseHexRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export interface ILiveMapStatusBadgePalette {
  backgroundColor: string;
  borderColor: string;
  color: string;
}

const buildLiveMapStatusBadgePaletteFromColor = (
  baseColor: string,
): ILiveMapStatusBadgePalette => ({
  borderColor: hexColorToRgba(baseColor, 0.38),
  backgroundColor: mixSrgbHexWithWhite(baseColor, 14),
  color: baseColor,
});

export const buildLiveMapDelayStatusBadgePalette = (
  status: RideDelayStatusEnum,
): ILiveMapStatusBadgePalette =>
  buildLiveMapStatusBadgePaletteFromColor(RideStatusDelayToColor[status]);

/** Live-map "Start on time" column (RYDE-7685): OnTime green, Late red, Early orange, No Data grey. */
export const RideStartStatusToColor = {
  [RideStartStatusEnum.OnTime]: greenMui._600,
  [RideStartStatusEnum.Late]: redMui.A400,
  [RideStartStatusEnum.Early]: orangeMui.A400,
} as const;

const RIDE_START_STATUS_NO_DATA_COLOR = blueGreyMui._400;

export const buildLiveMapRideStartStatusBadgePalette = (
  status: RideStartStatusEnum | null | undefined,
): ILiveMapStatusBadgePalette =>
  buildLiveMapStatusBadgePaletteFromColor(
    status ? RideStartStatusToColor[status] : RIDE_START_STATUS_NO_DATA_COLOR,
  );

/** Rides grid "Route Completion Status" (RYDE-8037): Completed green, Partial orange, NotDone red. */
export const RideRouteCompletionStatusToColor = {
  [RideRouteCompletionStatusEnum.Completed]: greenMui._600,
  [RideRouteCompletionStatusEnum.Partial]: orangeMui.A400,
  [RideRouteCompletionStatusEnum.NotDone]: redMui.A400,
} as const;

export const buildRideRouteCompletionStatusBadgePalette = (
  status: RideRouteCompletionStatusEnum | null | undefined,
): ILiveMapStatusBadgePalette =>
  buildLiveMapStatusBadgePaletteFromColor(
    status
      ? RideRouteCompletionStatusToColor[status]
      : RIDE_START_STATUS_NO_DATA_COLOR,
  );

export const rideStatusDelayToColorLeftLine = (
  status: RideDelayStatusEnum | null | undefined,
): LeftLineColor => {
  switch (status) {
    case RideDelayStatusEnum.NO_SHOW:
      return "darkRed";
    case RideDelayStatusEnum.DELAYED:
      return "red";
    case RideDelayStatusEnum.MAYBE_LATE:
    case RideDelayStatusEnum.EARLY:
      return "orange";
    case RideDelayStatusEnum.ON_TIME:
      return "normal";
    case RideDelayStatusEnum.FUTURE:
      return "normal";
    case RideDelayStatusEnum.PAST:
    case RideDelayStatusEnum.NOT_TRACKED:
      return "violet";
    default:
      return "normal";
  }
};

export const rideStatusDelayToImage = (
  status: RideDelayStatusEnum | null | undefined,
): JSX.Element | null => {
  switch (status) {
    case RideDelayStatusEnum.NO_SHOW:
    case RideDelayStatusEnum.DELAYED:
      return (
        <LiveMapDelayStatusIcon aria-hidden>
          <SVG_MUI.LiveMapDelayRedSVG />
        </LiveMapDelayStatusIcon>
      );
    case RideDelayStatusEnum.MAYBE_LATE:
      return (
        <LiveMapDelayStatusIcon aria-hidden>
          <SVG_MUI.LiveMapDelayYellowSVG />
        </LiveMapDelayStatusIcon>
      );
    case RideDelayStatusEnum.EARLY:
      return (
        <LiveMapDelayStatusIcon aria-hidden>
          <SVG_MUI.LiveMapDelayYellowSVG />
        </LiveMapDelayStatusIcon>
      );
    case RideDelayStatusEnum.ON_TIME:
      return (
        <LiveMapDelayStatusIcon aria-hidden>
          <SVG_MUI.LiveMapDelayGreenSVG />
        </LiveMapDelayStatusIcon>
      );
    default:
      return null;
  }
};

export const formatLiveMapDelayCounter = (
  status: RideDelayStatusEnum | null | undefined,
  timeLate: number | null | undefined,
): string | null => {
  switch (status) {
    case RideDelayStatusEnum.NO_SHOW:
      return "--";
    case RideDelayStatusEnum.ON_TIME:
    case RideDelayStatusEnum.MAYBE_LATE:
      return null;
    case RideDelayStatusEnum.DELAYED:
      if (timeLate === undefined || timeLate === null) {
        return null;
      }

      return `+${Math.abs(Math.round(timeLate))}`;
    case RideDelayStatusEnum.EARLY:
      if (timeLate === undefined || timeLate === null) {
        return null;
      }

      return `${Math.round(timeLate)}`;
    default:
      return null;
  }
};
